/**
 * Pure planning-mode query helpers — no Vue reactivity, so they unit-test cleanly
 * and can be reused from the store getters (which resolve ids to objects first).
 *
 * The eligibility and reachability rules here mirror the side-panel logic in
 * DashboardView.vue (`eligibleVehicles`, `repoDestNodes`, `activeAssignmentFor`).
 * Spec §7 forbids refactoring that flow, so the rules are intentionally duplicated
 * and must be kept in sync — acceptance criterion 11 requires the map and the side
 * panel to agree exactly.
 */

/** Node id where the vehicle ends up after current + queued work completes. */
export function effectiveDestinationNodeId(vehicle) {
  const queued = vehicle.queuedAssignments
  if (queued?.length > 0) return queued[queued.length - 1].destinationNodeId
  return vehicle.activeAssignment?.destinationNodeId ?? vehicle.currentNode?.id
}

/**
 * Eligibility of a single vehicle for a contract, with a human-readable reason
 * when it is not eligible (for the disabled-with-reason rows in the popover).
 * Returns `{ eligible: boolean, reason?: string }`.
 */
export function vehicleEligibility(contract, vehicle) {
  if (!contract?.requiredModes?.includes(vehicle.mode)) {
    return { eligible: false, reason: 'wrong mode' }
  }
  if (vehicle.cargoUnitCapacity < contract.cargoUnits) {
    return { eligible: false, reason: 'insufficient capacity' }
  }
  if (vehicle.status === 'IDLE') {
    return vehicle.currentNode?.id === contract.originNode?.id
      ? { eligible: true }
      : { eligible: false, reason: 'not at pickup location' }
  }
  if (vehicle.status === 'IN_TRANSIT') {
    return effectiveDestinationNodeId(vehicle) === contract.originNode?.id
      ? { eligible: true }
      : { eligible: false, reason: 'busy — arriving elsewhere' }
  }
  return { eligible: false, reason: 'unavailable' }
}

/**
 * Idle vehicles parked at *other* nodes that can validly reposition to `node`.
 * ROAD/RAIL are restricted to zones reachable from the vehicle's current zone;
 * SEA/AIR are unrestricted. Mirrors `repoDestNodes` in DashboardView.vue.
 */
export function repositionableVehiclesTo(node, fleet) {
  if (!node) return []
  return (fleet ?? []).filter(v => {
    if (v.status !== 'IDLE') return false
    const departureNodeId = v.effectiveEndNode?.id ?? v.currentNode?.id
    if (departureNodeId === node.id) return false
    if (!node.availableModes?.includes(v.mode)) return false
    if ((v.mode === 'ROAD' || v.mode === 'RAIL') && !v.reachableZoneIds?.includes(node.zoneId)) {
      return false
    }
    return true
  })
}

/** True when any vehicle has an active or queued assignment for the contract. */
function isContractAssigned(contractId, fleet) {
  return (fleet ?? []).some(v =>
    v.activeAssignment?.contractId === contractId ||
    v.queuedAssignments?.some(qa => qa.contractId === contractId)
  )
}

/**
 * Nodes a vehicle can reposition to: excludes its current departure node,
 * requires the node's availableModes to include the vehicle mode, and restricts
 * ROAD/RAIL to zones in the vehicle's reachableZoneIds. Mirrors `repoDestNodes`
 * in DashboardView.vue — keep in sync (criterion 11).
 */
export function repoDestNodes(vehicle, allNodes) {
  if (!vehicle) return []
  const departureNodeId = vehicle.effectiveEndNode?.id ?? vehicle.currentNode?.id
  return (allNodes ?? []).filter(n => {
    if (!n.availableModes?.includes(vehicle.mode)) return false
    if (n.id === departureNodeId) return false
    if ((vehicle.mode === 'ROAD' || vehicle.mode === 'RAIL') && !vehicle.reachableZoneIds?.includes(n.zoneId)) {
      return false
    }
    return true
  })
}

/**
 * Contracts an idle vehicle can take on right now from its current node:
 * available (unclaimed) contracts plus the player's own claimed-but-unassigned
 * contracts, filtered to the vehicle's mode/capacity and co-located origin.
 * MVP shows only co-located vehicles (spec §9 Q1) — reposition-then-claim is deferred.
 */
export function reachableContractsFor(vehicle, contracts, myContracts, fleet) {
  if (!vehicle || vehicle.status !== 'IDLE') return []
  const nodeId = vehicle.currentNode?.id
  const matches = c =>
    c.requiredModes?.includes(vehicle.mode) &&
    vehicle.cargoUnitCapacity >= c.cargoUnits &&
    c.originNode?.id === nodeId
  const available = (contracts ?? []).filter(matches)
  const claimedUnassigned = (myContracts ?? []).filter(
    c => matches(c) && !isContractAssigned(c.id, fleet)
  )
  return [...available, ...claimedUnassigned]
}
